import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { API, TOKEN_KEY } from "@/lib/api";
import {
    Film,
    Clapperboard,
    Scissors,
    Download,
    FileText,
    Upload,
    AudioLines,
    Sparkles,
    CircleCheck,
    AlertTriangle,
    Loader2,
} from "lucide-react";

const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

const sizeLabel = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Google Doc URL -> direct PDF export URL.
//  https://docs.google.com/document/d/DOC_ID/edit?... -> .../export?format=pdf
const toPdfExportUrl = (url) => {
    const match = url.match(/\/document\/d\/([^/]+)/);
    if (!match) return url;
    return `https://docs.google.com/document/d/${match[1]}/export?format=pdf`;
};

const STAGES = [
    { label: "Uploaded", icon: Upload },
    { label: "Cleaning Audio", icon: AudioLines },
    { label: "Adding Captions & B-Roll", icon: Sparkles },
    { label: "Ready", icon: CircleCheck },
];

const NODE_STYLE = {
    complete: {
        wrap: "border-transparent bg-gradient-to-br from-cyan-400 to-purple-500",
        icon: "text-white",
        label: "text-white/80",
    },
    ready: {
        wrap: "border-transparent bg-gradient-to-br from-emerald-400 to-emerald-600",
        icon: "text-white",
        label: "text-emerald-200",
    },
    active: {
        wrap: "border-cyan-400/70 bg-white/[0.04]",
        icon: "text-cyan-300",
        label: "text-cyan-200",
    },
    pending: {
        wrap: "border-white/10 bg-white/[0.04]",
        icon: "text-white/30",
        label: "text-white/35",
    },
    locked: {
        wrap: "border-white/10 bg-white/[0.04]",
        icon: "text-white/25",
        label: "text-white/30",
    },
};

const JourneyNode = ({ icon: Icon, label, state, celebrate }) => {
    const reduce = useReducedMotion();
    const s = NODE_STYLE[state];
    const isActive = state === "active";
    const burst = celebrate && state === "ready" && !reduce;

    let animate;
    let transition;
    if (burst) {
        animate = { scale: [1, 1.35, 1] };
        transition = { duration: 1.2, ease: "easeOut" };
    } else if (isActive && !reduce) {
        animate = { scale: [1, 1.12, 1] };
        transition = { duration: 1.4, repeat: Infinity, ease: "easeInOut" };
    }

    return (
        <div className="flex w-16 flex-none flex-col items-center gap-2 sm:w-24">
            <div className="relative">
                {burst && (
                    <>
                        <motion.span
                            className="pointer-events-none absolute inset-0 rounded-full border-2 border-emerald-300"
                            initial={{ scale: 1, opacity: 0.75 }}
                            animate={{ scale: 2.6, opacity: 0 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                        />
                        <motion.span
                            className="pointer-events-none absolute inset-0 rounded-full border border-emerald-200"
                            initial={{ scale: 1, opacity: 0.6 }}
                            animate={{ scale: 2, opacity: 0 }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.15 }}
                        />
                    </>
                )}
                <motion.div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border ${s.wrap}`}
                    animate={animate}
                    transition={transition}
                >
                    <Icon className={`h-5 w-5 ${s.icon}`} strokeWidth={2} />
                </motion.div>
            </div>
            <span
                className={`text-center text-[10px] font-medium leading-tight sm:text-xs ${s.label}`}
            >
                {label}
            </span>
        </div>
    );
};

const Connector = ({ filled }) => (
    <div className="mt-[19px] h-1 flex-1 overflow-hidden rounded-full bg-white/10">
        <motion.div
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-purple-500"
            initial={false}
            animate={{ width: filled ? "100%" : "0%" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        />
    </div>
);

const Row = ({ video, celebrate, onUploadAgain, onViewFinished, ackingId, trayMode = false }) => {
    const auto = video.folder === "auto-broll";
    const reduce = useReducedMotion();
    const rootRef = useRef(null);
    // Real fields/values from GET /api/videos.
    const isFailed = video.status === "failed";
    // Processing-tray model: a card is only ever "done" (green Ready node) once
    // the server proves it reached Finished Videos (ready_in_finished_videos).
    // In tray mode that boolean is the ONLY completion signal — a done-status
    // video not yet handed off is "Finalizing", not Ready. Outside the tray
    // (Finished Videos / other consumers) behavior is UNCHANGED: the journey
    // still completes on status === "done".
    const readyHandoff = video.ready_in_finished_videos === true;
    const isDone = trayMode ? readyHandoff : video.status === "done";
    // Finished-video handoff CTA: shown ONLY once the server proves the video is
    // delivered (ready) AND the one-time completion flag is still set. `acking`
    // disables the button while its acknowledgment POST is in flight.
    const showHandoffCta =
        trayMode && readyHandoff && video.new_completion === true;
    const acking = ackingId === video.id;
    const finishedUrl = (video.finished_video_url || "").trim();
    const docUrl = (video.doc_url || "").trim();
    // Download actions are gated on a successful (done) render AND never shown
    // for a failed video. Tray mode NEVER shows Dashboard download controls
    // (Finished Videos owns downloads).
    const showVideoBtn = !trayMode && isDone && !isFailed && finishedUrl !== "";
    const showDocBtn = !trayMode && isDone && !isFailed && docUrl !== "";
    // Tray-only plain-word status label (never an unlabeled icon). Failed cards
    // are labeled by the failure panel itself. Any UNKNOWN/malformed status maps
    // to "Needs Attention" — the card is never dropped for an unknown status.
    const trayLabel =
        readyHandoff
            ? "Ready"
            : video.status === "processing"
              ? "Processing"
              : video.status === "done"
                ? "Finalizing"
                : "Needs Attention";
    // Download Video goes through a backend proxy that streams the file with a
    // friendly Content-Disposition filename (cross-origin hosts ignore the
    // HTML5 `download` attribute). dl=1 handling is done server-side.
    const videoHref = showVideoBtn
        ? `${API}/videos/${video.id}/download?token=${encodeURIComponent(
              localStorage.getItem(TOKEN_KEY) || "",
          )}`
        : "";
    const docPdfHref = showDocBtn ? toPdfExportUrl(docUrl) : "";

    // Purely-visual cycle between the two middle stages while processing.
    // A failed (or done) video must NOT animate — the effect returns early and
    // cleans up any prior interval when the status transitions to failed/done.
    const [activeStage, setActiveStage] = useState(2);
    useEffect(() => {
        if (isDone || isFailed) return undefined;
        const id = setInterval(
            () => setActiveStage((s) => (s === 2 ? 3 : 2)),
            1800,
        );
        return () => clearInterval(id);
    }, [isDone, isFailed]);

    // Node index is 1-based (1..4).
    const stageState = (idx) => {
        if (isDone) return idx === 4 ? "ready" : "complete";
        if (idx === 1) return "complete"; // Uploaded — always done
        if (idx === 2) return activeStage === 2 ? "active" : "complete";
        if (idx === 3) return activeStage === 3 ? "active" : "pending";
        return "locked"; // Ready — locked until status === "done"
    };
    const segFilled = (idx) => {
        const next = stageState(idx + 1);
        return next === "complete" || next === "ready" || next === "active";
    };

    // One-time celebration: gently scroll the row into view when it celebrates.
    useEffect(() => {
        if (celebrate && !reduce) {
            rootRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [celebrate, reduce]);

    return (
        <motion.div
            ref={rootRef}
            layout
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="rounded-lg border border-white/10 bg-surface-panel p-4 sm:p-5"
        >
            {/* Top: thumbnail + title + meta */}
            <div className="flex items-center gap-4 sm:gap-5">
                <div
                    className="relative flex h-16 w-full max-w-[96px] flex-none items-center justify-center overflow-hidden rounded-xl sm:h-16 sm:w-28"
                    style={{
                        background: `linear-gradient(135deg, ${video.thumbnail_color}33, ${video.thumbnail_color}11)`,
                        boxShadow: `inset 0 0 0 1px ${video.thumbnail_color}55`,
                    }}
                >
                    <Film
                        className="h-8 w-8"
                        style={{ color: video.thumbnail_color }}
                    />
                </div>

                <div className="min-w-0 flex-1">
                    <p className="truncate text-lg font-semibold text-white sm:text-xl">
                        {video.name}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-white/50">
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1">
                            {auto ? (
                                <Clapperboard className="h-4 w-4" />
                            ) : (
                                <Scissors className="h-4 w-4" />
                            )}
                            {auto ? "Auto B-Roll" : "No B-Roll"}
                        </span>
                        <span>{sizeLabel(video.size)}</span>
                        <span>•</span>
                        <span>{timeAgo(video.created_at)}</span>
                    </div>
                </div>
            </div>

            {/* Tray-only status label (plain words, never an unlabeled icon). */}
            {trayMode && !isFailed && (
                <div className="mt-3">
                    <span
                        data-testid="tray-status-label"
                        className="inline-flex items-center rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/70"
                    >
                        {trayLabel}
                    </span>
                </div>
            )}

            {/* Terminal FAILED state: no journey animation, no downloads — a
                clear Failed panel with safe copy and an upload-again action. */}
            {isFailed ? (
                <div
                    data-testid="video-failed-panel"
                    className="mt-5 rounded-lg border border-rose-400/25 bg-rose-500/[0.06] p-4 sm:p-5"
                >
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-rose-400/40 bg-rose-500/10">
                            <AlertTriangle className="h-5 w-5 text-rose-300" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="text-base font-semibold text-rose-100">
                                Failed
                            </p>
                            <p
                                data-testid="video-failed-message"
                                className="mt-1 text-sm leading-relaxed text-rose-100/70"
                            >
                                We couldn't finish this video. Please try uploading
                                it again. If the problem continues, contact support.
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex">
                        <button
                            type="button"
                            data-testid="video-upload-again-button"
                            onClick={onUploadAgain}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-rose-400/40 bg-rose-500/10 min-h-11 px-4 text-sm font-semibold text-rose-100 transition-colors hover:border-rose-300/60 hover:bg-rose-500/20 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-300"
                        >
                            <Upload className="h-4 w-4 flex-none" /> Upload another video
                        </button>
                    </div>
                </div>
            ) : (
            <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-4">
                {/* Journey bar */}
                <div className="flex items-start gap-2 sm:gap-3 lg:flex-1">
                    {STAGES.map((s, i) => (
                        <React.Fragment key={s.label}>
                            <JourneyNode
                                icon={s.icon}
                                label={s.label}
                                state={stageState(i + 1)}
                                celebrate={celebrate}
                            />
                            {i < STAGES.length - 1 && (
                                <Connector filled={segFilled(i + 1)} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Download buttons (done only) */}
                {(showVideoBtn || showDocBtn) && (
                    <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 lg:flex-none lg:justify-end">
                        {showVideoBtn && (
                            <a
                                href={videoHref}
                                download
                                data-testid="download-video-button"
                                className="inline-flex min-w-[9rem] flex-1 items-center justify-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 min-h-11 px-4 text-sm font-semibold text-cyan-200 transition-colors hover:border-cyan-300/60 hover:bg-cyan-500/20 hover:text-white lg:flex-none"
                            >
                                <Download className="h-4 w-4 flex-none" /> Download Video
                            </a>
                        )}
                        {showDocBtn && (
                            <a
                                href={docPdfHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                data-testid="download-doc-button"
                                className="inline-flex min-w-[9rem] flex-1 items-center justify-center gap-2 rounded-full border border-purple-400/40 bg-purple-500/10 min-h-11 px-4 text-sm font-semibold text-purple-200 transition-colors hover:border-purple-300/60 hover:bg-purple-500/20 hover:text-white lg:flex-none"
                            >
                                <FileText className="h-4 w-4 flex-none" /> Download Doc
                            </a>
                        )}
                    </div>
                )}

                {/* Finished-video handoff CTA (tray mode only). Text + icon
                    convey the signal; the green glow only SUPPORTS it, so it is
                    readable without color perception. */}
                {showHandoffCta && (
                    <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 lg:flex-none lg:justify-end">
                        <button
                            type="button"
                            data-testid="view-finished-video-button"
                            aria-label="View your finished video"
                            aria-busy={acking}
                            disabled={acking}
                            onClick={() => onViewFinished?.(video)}
                            className="inline-flex min-w-[12rem] flex-1 items-center justify-center gap-2 rounded-full border border-emerald-400/50 bg-emerald-500/15 min-h-11 px-4 text-sm font-semibold text-emerald-100 transition-colors hover:border-emerald-300/70 hover:bg-emerald-500/25 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-300 disabled:cursor-not-allowed disabled:opacity-60 lg:flex-none"
                        >
                            {acking ? (
                                <Loader2 className="h-4 w-4 flex-none animate-spin" />
                            ) : (
                                <CircleCheck className="h-4 w-4 flex-none" />
                            )}
                            View your finished video
                        </button>
                    </div>
                )}
            </div>
            )}
        </motion.div>
    );
};

export const VideoHistoryList = ({ videos, celebrationId, onUploadAgain, onViewFinished, ackingId, trayMode = false }) => {
    if (!videos || videos.length === 0) {
        return (
            <div
                data-testid="dashboard-video-history-empty"
                className="rounded-lg border border-dashed border-white/12 bg-surface-panel p-14 text-center"
            >
                <Film className="mx-auto h-12 w-12 text-white/30" />
                <p className="mt-4 text-xl font-semibold text-white/75">
                    No videos yet
                </p>
                <p className="mt-1.5 text-base text-white/40">
                    Upload your first clip to see it appear here.
                </p>
            </div>
        );
    }
    return (
        <div data-testid="dashboard-video-history" className="space-y-3">
            <AnimatePresence initial={false}>
                {videos.map((v) => (
                    <Row
                        key={v.id}
                        video={v}
                        celebrate={celebrationId === v.id}
                        onUploadAgain={onUploadAgain}
                        onViewFinished={onViewFinished}
                        ackingId={ackingId}
                        trayMode={trayMode}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};
